import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, Check, Loader2, Plug } from 'lucide-react'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useAnalytics } from 'hooks'
import { useAppSelector } from 'modules/core'
import { selectBusinessId } from 'modules/authentication'
import { oauthUtils } from 'modules/oauth/utils/oauth-utils'
import { useLinkedInConnect } from 'modules/oauth/hooks/useLinkedInConnect'
import { Button } from 'components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import { usePlatformConnections } from '../hooks/usePlatformConnections'
import { PlatformIcon, platformLabel } from 'components/PlatformIcon'
import type { LivePlatform } from '../store/idea-store'

interface ConnectToPublishDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Platforms in the post that still need OAuth. Read once when the dialog opens. */
  missingPlatforms: LivePlatform[]
  /** The full in-strip selection — used for the "X of Y connected" copy and as the
   *  default payload when the user advances. */
  selectedPlatforms: LivePlatform[]
  /** Project id for analytics attribution. */
  projectId: string
  /** Fires when the user is ready to proceed to the date/time picker. The array
   *  is the final set of platforms to schedule — equal to `selectedPlatforms`
   *  on Continue, or `selectedPlatforms` minus still-missing on Skip. */
  onReady: (finalPlatforms: LivePlatform[]) => void
  /** When true, the user must connect EVERY missing platform — the "Skip and
   *  schedule the rest" action is hidden. Used by the composer's PostModal
   *  entry path where the post's platform set is the source of truth and
   *  scheduling a subset isn't allowed. Default false preserves the legacy
   *  "skip is fine" behavior for existing call sites. */
  requireAll?: boolean
}

/**
 * Pre-Schedule connect gate for Post mode. When the user clicks Schedule with
 * any in-strip platform missing its OAuth, we open this dialog instead of
 * jumping straight to the date/time picker. The user can connect each missing
 * platform inline; auto-advances to ScheduleDialog once the last one lands.
 *
 * "Skip and schedule the rest" drops the still-missing platforms from the
 * schedule payload — useful when the user wants to publish to what they have
 * connected and come back for the rest later.
 */
export function ConnectToPublishDialog({
  open,
  onOpenChange,
  missingPlatforms: initialMissing,
  selectedPlatforms,
  projectId,
  onReady,
  requireAll = false,
}: ConnectToPublishDialogProps) {
  const { track } = useAnalytics()
  const businessId = useAppSelector(selectBusinessId)
  const { connections, refetch: refetchConnections } = usePlatformConnections()
  const { resolveConnectProvider, chooser } = useLinkedInConnect()
  const [connectingPlatform, setConnectingPlatform] = useState<LivePlatform | null>(null)
  const promptShownRef = useRef(false)

  // Snapshot the missing set at open time so the row list doesn't shrink as
  // the user connects platforms — we want each row to flip to a green ✓ chip
  // in place, not vanish.
  const [snapshot, setSnapshot] = useState<LivePlatform[]>(initialMissing)
  useEffect(() => {
    if (open) {
      setSnapshot(initialMissing)
      promptShownRef.current = false
    }
  }, [open, initialMissing])

  // Live remaining set (re-computed from `connections` as OAuth lands).
  const stillMissing = useMemo(
    () =>
      snapshot.filter(p => {
        const conn = connections[p]
        return !!conn?.needsConnection && !conn.connected
      }),
    [snapshot, connections]
  )

  const totalSelected = selectedPlatforms.length
  const totalConnected = totalSelected - stillMissing.length

  // Fire the prompt-shown event once per dialog open, once we have the values.
  useEffect(() => {
    if (!open || promptShownRef.current) return
    promptShownRef.current = true
    track(AnalyticsEvents.Composer.COMPOSER_SCHEDULE_CONNECT_PROMPT_SHOWN, {
      project_id: projectId,
      missing_count: initialMissing.length,
      missing: initialMissing,
      selected_count: totalSelected,
    })
  }, [open, projectId, initialMissing, totalSelected, track])

  // Auto-advance when the last missing platform connects. Guard with `open`
  // so a stale connection state on re-open doesn't fire onReady immediately.
  const advancedRef = useRef(false)
  useEffect(() => {
    if (!open) {
      advancedRef.current = false
      return
    }
    if (advancedRef.current) return
    if (snapshot.length > 0 && stillMissing.length === 0) {
      advancedRef.current = true
      onReady(selectedPlatforms)
    }
  }, [open, snapshot.length, stillMissing.length, selectedPlatforms, onReady])

  const handleConnect = useCallback(
    async (platform: LivePlatform) => {
      const conn = connections[platform]
      if (!conn?.oauthProvider || !businessId || connectingPlatform) return
      // LinkedIn fronts two OAuth apps (personal `linkedin` / company `linkedin_org`).
      // Open the shared chooser first (when gated on); null = dismissed → abort before
      // the spinner. Other providers pass through. Both LinkedIn variants land on the
      // single `linkedin` connection, so refetchConnections below is unaffected.
      const oauthProvider = await resolveConnectProvider(conn.oauthProvider)
      if (!oauthProvider) return
      track(AnalyticsEvents.Composer.COMPOSER_SCHEDULE_CONNECT_TAPPED, {
        project_id: projectId,
        platform,
      })
      setConnectingPlatform(platform)
      try {
        await oauthUtils.openOAuthPopup(oauthProvider, {
          businessId,
          source: 'composer',
          track,
        })
        await refetchConnections()
      } finally {
        setConnectingPlatform(null)
      }
    },
    [
      connections,
      businessId,
      connectingPlatform,
      refetchConnections,
      track,
      projectId,
      resolveConnectProvider,
    ]
  )

  const handleContinue = useCallback(() => {
    onReady(selectedPlatforms)
  }, [onReady, selectedPlatforms])

  const handleSkip = useCallback(() => {
    const stillMissingSet = new Set(stillMissing)
    const survivors = selectedPlatforms.filter(p => !stillMissingSet.has(p))
    track(AnalyticsEvents.Composer.COMPOSER_SCHEDULE_CONNECT_SKIPPED, {
      project_id: projectId,
      skipped: stillMissing,
    })
    onReady(survivors)
  }, [stillMissing, selectedPlatforms, onReady, projectId, track])

  // Footer state
  const canContinue = stillMissing.length === 0
  // Hide skip when nothing would survive (user must connect at least one),
  // or when the caller has opted into all-or-nothing semantics via requireAll
  // (composer entry path — the post's platform set is fixed and scheduling
  // a subset isn't allowed).
  const canSkip = !requireAll && stillMissing.length < totalSelected

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="md" variant="dark" withCloseButton>
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-acid">
              <Plug size={22} />
            </div>
            <DialogTitle variant="dark">Connect to post</DialogTitle>
            <DialogDescription variant="dark">
              {totalConnected} of {totalSelected} connected. Connect {stillMissing.length} more to
              schedule {stillMissing.length === 1 ? 'it' : 'them all'}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {snapshot.map(p => {
              const label = platformLabel(p)
              const conn = connections[p]
              const isConnected = !!conn && (!conn.needsConnection || conn.connected)
              const isConnecting = connectingPlatform === p

              return (
                <div
                  key={p}
                  className="flex items-center gap-3 rounded-[10px] border border-line-bright bg-bg-3 px-3 py-2.5"
                >
                  {/* Platform icon disc */}
                  <PlatformIcon channel={p} size={32} />

                  <span className="flex-1 truncate text-[13px] font-medium text-paper">
                    {label}
                  </span>

                  {isConnected ? (
                    <span className="mono flex items-center gap-1 rounded-full bg-acid/15 px-2 py-0.5 text-[10px] tracking-[0.18em] text-acid uppercase">
                      <Check size={11} strokeWidth={3} />
                      Connected
                    </span>
                  ) : (
                    <Button
                      variant="t3-tertiary"
                      size="t3-sm"
                      onClick={() => void handleConnect(p)}
                      disabled={isConnecting || !!connectingPlatform}
                    >
                      {isConnecting ? (
                        <>
                          <Loader2 size={12} className="animate-spin" />
                          Connecting…
                        </>
                      ) : (
                        <>
                          Connect
                          <ArrowRight size={12} />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              variant="t3-primary"
              size="t3-md"
              onClick={handleContinue}
              disabled={!canContinue}
              className="w-full"
            >
              {canContinue ? (
                <>
                  Continue to schedule
                  <ArrowRight size={14} />
                </>
              ) : (
                <>Connect {stillMissing.length} more to continue</>
              )}
            </Button>
            {canSkip && (
              <Button variant="t3-tertiary" size="t3-md" onClick={handleSkip} className="w-full">
                Skip and schedule the rest ({selectedPlatforms.length - stillMissing.length})
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {chooser}
    </>
  )
}
